import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  capitalize,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "material-react-table";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { InitiationDayFilter, StatCard } from "../AnchorNew/AnchorDashboard";
import CustomButton from "../Common/CustomButton";
import EllipsisedCell, { formatNumberAndCurrency } from "../Common/EllipsisedCell";
import FiltersBars from "../Common/FiltersBars";
import { LoggedInSkeleton } from "../Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "../Common/TopNavBar";
import useFetch from "../Common/useFetch";
import { userContext } from "../Contexts/userContext";
import ReactTable from "../ReactTable";
import { downloadExcelFromBuffer } from "../Vendor/UploadFileCommonDialog";
import { datefilterFn } from "../Vendor/UploadInvoice";
import UploadCreditNote from "./UploadCreditNote";
import VendorSideFilter from "./VendorSideFilter";

export enum CN_Status_Type {
  CN_PENDING = "CN pending",
  CN_VERIFIED = "CN verified",
  CN_REJECTED = "CN rejected",
  REVIEW_PENDING = "Review pending",
}
export type AllowedFilterKeysVendorSide =
  | "grnNumber"
  | "invoiceNumber"
  | "vendorCode"
  | "warehouseName"
  | "earlyPayDate"
  | "cnStatus";

const formatDataOverallSummary = (data: any) => {
  if (!data) return [];
  return [
    {
      title: "Invoice Paid Early",
      value: formatNumberAndCurrency(data.invoicesPaidEarlyCount, false),
    },
    {
      title: "Value of Invoice Paid Early",
      value: formatNumberAndCurrency(data.valueOfInvoicesPaidEarly, true),
    },
    {
      title: "Total Disbursed Amount",
      value: formatNumberAndCurrency(data.totalDisbursedAmount, true),
    },
    {
      title: "Total Discount",
      value: formatNumberAndCurrency(data.totalDiscountAmount, true),
    },
  ];
};

const formatDataCBSummary = (data: any) => {
  if (!data) return [];
  return [
    {
      title: "Scootsy Balance",
      value: formatNumberAndCurrency(data.scootsyBalance, true),
    },
    {
      title: "Closing Balance (All Entity)",
      value: formatNumberAndCurrency(data.closingBalanceAllEntity, true),
    },
    {
      title: "Entity Wise Hold",
      value: formatNumberAndCurrency(data.entityWiseHold, true),
    },
    {
      title: "BDPO Hold",
      value: formatNumberAndCurrency(data.bdpoHold, true),
    },
  ];
};

export interface VendorSideData {
  grnNumber: string;
  grnDate: string;
  invoiceNumber: string;
  amountDisbursed: string;
  discountAmount: string;
  grnAmount: string;
  earlyPaymentStatus: string;
  earlyPayDate: string;
  warehouseName: string;
  cnAgeing: number;
  cnAgeingStatus: string;
  organizationName: string;
  invoiceAmount: string;
  vendorCode: string;
  gstin: string;
  invoiceAccountingDate: string;
  creditPeriod: number;
  dueDate: string;
  invoiceDate: string;
  cnStatus: CN_Status_Type;
  cnNumber: string;
  isCreditNote: boolean;
  id: string;
  vendorCnDate: string;
  vendorCnNumber: string;
}

export default function VendorSideDashboard() {
  const { actor } = useContext(userContext);
  const location = useLocation<any>();
  const [isUploadingCreditNote, setIsUploadingCreditNote] = useState<boolean>(false);
  const [allInvoices, setAllInvoices] = useState<VendorSideData[]>([]);
  const [isLoadingAllInvoices, setIsLoadingAllInvoices] = useState<boolean>(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

  // Reset upload state when navigating to dashboard
  useEffect(() => {
    setIsUploadingCreditNote(false);
  }, [location.key]);

  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<AllowedFilterKeysVendorSide, string[]>>({
    grnNumber: [],
    invoiceNumber: [],
    vendorCode: [],
    warehouseName: [],
    earlyPayDate: [],
    cnStatus: [],
  });

  const [selectedTime, setSelectedTime] = useState<InitiationDayFilter>(InitiationDayFilter.ALL);
  const [overallSummary, setOverallSummary] = useState<Record<string, number>>({
    invoicesPaidEarlyCount: 0,
    valueOfInvoicesPaidEarly: 0,
    totalDisbursedAmount: 0,
    totalDiscountAmount: 0,
  });
  const [cbSummary, setCbSummary] = useState<Record<string, number>>({
    scootsyBalance: 0,
    closingBalanceAllEntity: 0,
    entityWiseHold: 0,
    bdpoHold: 0,
    lastRefreshedAt: null,
  });

  const handleTimeFilter = (label: InitiationDayFilter) => {
    setSelectedTime(label);
  };
  const overallSummaryCards = formatDataOverallSummary(overallSummary);
  const overallSummaryCardCount = overallSummaryCards.length;
  const overallSummaryCardSize = overallSummaryCardCount >= 5 ? 12 / 5 : 12 / overallSummaryCardCount;

  const cbSummaryCards = formatDataCBSummary(cbSummary);
  const cbSummaryCardCount = cbSummaryCards.length;
  const cbSummaryCardSize = cbSummaryCardCount >= 5 ? 12 / 5 : 12 / cbSummaryCardCount;

  const columns = useMemo<MRT_ColumnDef<VendorSideData>[]>(
    () => [
      {
        header: "GRN Number",
        accessorKey: "grnNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
        minSize: 250,
      },
      {
        header: "GRN Date",
        accessorKey: "grnDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.grnDate, filterValue);
        },
        accessorFn: (row) => (row.grnDate ? new Date(row.grnDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Invoice Number",
        accessorKey: "invoiceNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
        minSize: 250,
      },
      {
        header: "Amount disbursed",
        accessorKey: "amountDisbursed",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Discount amount",
        accessorKey: "discountAmount",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
        minSize: 250,
        filterVariant: "range",
      },
      {
        header: "Invoice Date",
        accessorKey: "invoiceDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.invoiceDate, filterValue);
        },
        accessorFn: (row) => (row.invoiceDate ? new Date(row.invoiceDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Invoice amount",
        accessorKey: "invoiceAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Warehouse Name",
        accessorKey: "warehouseName",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "CN status",
        accessorKey: "cnStatus",
        size: 200,
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Vendor CN Date",
        accessorKey: "vendorCnDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.vendorCnDate, filterValue);
        },
        accessorFn: (row) => (row.vendorCnDate ? new Date(row.vendorCnDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },

      {
        header: "Vendor CN Number",
        accessorKey: "vendorCnNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Early Payment Status",
        accessorKey: "earlyPaymentStatus",
        filterVariant: "multi-select",
        minSize: 280,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Early pay date",
        accessorKey: "earlyPayDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.earlyPayDate, filterValue);
        },
        accessorFn: (row) => (row.earlyPayDate ? new Date(row.earlyPayDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },

      {
        header: "CN ageing",
        accessorKey: "cnAgeing",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="number" />,
      },
      {
        header: "CN ageing status",
        accessorKey: "cnAgeingStatus",
        minSize: 200,
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Organization Name",
        accessorKey: "organizationName",
        minSize: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "GRN Amount",
        accessorKey: "grnAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Vendor Code",
        accessorKey: "vendorCode",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "GSTIN",
        accessorKey: "gstin",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Invoice Accounting Date",
        accessorKey: "invoiceAccountingDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.invoiceAccountingDate, filterValue);
        },
        accessorFn: (row) => (row.invoiceAccountingDate ? new Date(row.invoiceAccountingDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Credit Period",
        accessorKey: "creditPeriod",
        filterVariant: "range",
        minSize: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="number" />,
      },
      {
        header: "Due Date",
        accessorKey: "dueDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.dueDate, filterValue);
        },
        accessorFn: (row) => (row.dueDate ? new Date(row.dueDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
    ],
    []
  );

  const fetchAllData = async () => {
    listAllData();
    getClosingBalanceSummary();
  };

  useEffect(() => {
    fetchAllData();
  }, [appliedFilters, selectedTime]);

  const getClosingBalanceSummary = async () => {
    await useFetch("/closing-balance/summary", "GET", {
      config: { params: { vendorId: actor.actorId } },
      failureMessage: "Failed to fetch closing balance summary",
      thenCallBack: (response) => {
        setCbSummary(response.data.result.summary || null);
      },
      errorCallback: (error) => {
        console.error("Error fetching closing balance summary:", error);
      },
    });
  };

  const listAllData = async () => {
    // API call to fetch data
    setIsLoadingAllInvoices(true);
    const startDate = appliedFilters.earlyPayDate[0]
      ? moment(appliedFilters.earlyPayDate[0]).format("DD-MM-YYYY")
      : null;
    const endDate = appliedFilters.earlyPayDate[1] ? moment(appliedFilters.earlyPayDate[1]).format("DD-MM-YYYY") : null;
    await useFetch(`/invoices/list/2/${actor.actorId}`, "POST", {
      data: {
        grnNumber: appliedFilters.grnNumber,
        invoiceNumber: appliedFilters.invoiceNumber,
        vendorCode: appliedFilters.vendorCode,
        warehouseName: appliedFilters.warehouseName,
        earlyPayStartDate: startDate,
        earlyPayEndDate: endDate,
        dateFilter: selectedTime,
        cnStatus: appliedFilters.cnStatus,
      },
      failureMessage: "Failed to fetch data",
      thenCallBack: (response) => {
        setAllInvoices(response.data.result.invoices || []);
        setOverallSummary(response.data.result.overallSummary || null);
        setIsLoadingAllInvoices(false);
      },
      errorCallback: (error) => {
        console.error("Error fetching data:", error);
        setIsLoadingAllInvoices(false);
      },
    });
  };

  const handleDownloadReport = async () => {
    setIsDownloadingReport(true);
    const startDate = appliedFilters.earlyPayDate[0]
      ? moment(appliedFilters.earlyPayDate[0]).format("DD-MM-YYYY")
      : null;
    const endDate = appliedFilters.earlyPayDate[1] ? moment(appliedFilters.earlyPayDate[1]).format("DD-MM-YYYY") : null;
    await useFetch(`/invoices/list/2/${actor.actorId}/download`, "POST", {
      data: {
        grnNumber: appliedFilters.grnNumber || [],
        invoiceNumber: appliedFilters.invoiceNumber || [],
        vendorCode: appliedFilters.vendorCode || [],
        warehouseName: appliedFilters.warehouseName || [],
        earlyPayStartDate: startDate,
        earlyPayEndDate: endDate,
        dateFilter: selectedTime,
        cnStatus: appliedFilters.cnStatus || [],
      },
      failureMessage: "Failed to download report",
      thenCallBack: (response) => {
        downloadExcelFromBuffer(response.data.result.invoiceReport, response.data.result.fileName);
        setIsDownloadingReport(false);
      },
      catchCallBack: () => {
        setIsDownloadingReport(false);
      },
    });
  };

  const handleDownloadCN = async (invoiceId: string) => {
    await useFetch(`/invoices/credit-note/download/${invoiceId}`, "GET", {
      failureMessage: "Failed to download Credit Note",
      thenCallBack: (response) => {
        downloadExcelFromBuffer(response.data.result.creditNoteFile, response.data.result.fileName);
      },
    });
  };

  return (
    <LoggedInSkeleton
      topBarButtons={getReconTopBarButtons("Upload", actor.name, location?.state?.openCollapseOb, actor)}
    >
      {isUploadingCreditNote ? (
        <UploadCreditNote setIsUploadingCreditNote={setIsUploadingCreditNote} fetchAllData={fetchAllData} />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              p: 2,
              mt: 1,
              backgroundColor: "#fff",
              borderRadius: 1,
              border: "1px solid #E5E5E5",
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Upload Credit note
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select warehouse, invoices and upload credit note.
              </Typography>
            </Box>
            <CustomButton
              variant="contained"
              onClick={() => {
                setIsUploadingCreditNote(true);
              }}
              sx={{ flexShrink: 0 }}
            >
              Upload Credit Note
            </CustomButton>
          </Box>
          <Accordion
            elevation={0}
            sx={{
              my: 1,
              "&.Mui-expanded": {
                my: 1,
              },
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              sx={{
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  mb: 0,
                  mt: 1.5,
                },
                "&.Mui-expanded": {
                  minHeight: "unset",
                },
              }}
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: 20 }}>
                  Closing Balance Summary
                </Typography>
                <Chip
                  label={
                    cbSummary.lastRefreshedAt
                      ? `Last refreshed at ${moment(cbSummary.lastRefreshedAt).format("DD/MM/YY h:mm A")} IST`
                      : "Loading.."
                  }
                  sx={{
                    color: "#0288D1",
                    backgroundColor: "#E3F2FD",
                    borderRadius: "4px",
                    "& .MuiChip-label": { fontSize: "12px", fontWeight: 600 },
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {cbSummaryCards.map(({ title, value }, index) => (
                  <Grid key={index} item xs={12} sm={6} md={cbSummaryCardSize}>
                    <StatCard title={title} value={value} />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion
            elevation={0}
            sx={{
              my: 1,
              "&.Mui-expanded": {
                my: 1,
              },
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              sx={{
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  mb: 0,
                  mt: 1.5,
                },
                "&.Mui-expanded": {
                  minHeight: "unset",
                },
              }}
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2-content"
              id="panel2-header"
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: 20 }}>
                Overall Summary
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {overallSummaryCards.map(({ title, value }, index) => (
                  <Grid key={index} item xs={12} sm={6} md={overallSummaryCardSize}>
                    <StatCard title={title} value={value} />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Box bgcolor={"#fff"}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Account Overview
              </Typography>
              <Stack direction="row" spacing={2}>
                <CustomButton
                  variant="outlined"
                  onClick={() => {
                    setOpenFilter(true);
                  }}
                  disabled={false}
                  // startIcon={false ? <CircularProgress size={20} /> : null}
                >
                  Filter
                </CustomButton>
                <CustomButton
                  variant="outlined"
                  onClick={handleDownloadReport}
                  disabled={isDownloadingReport}
                  startIcon={isDownloadingReport ? <CircularProgress size={20} /> : null}
                >
                  Download Report
                </CustomButton>
              </Stack>
            </Box>
            <Divider />
            <FiltersBars
              appliedFilters={appliedFilters}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setAppliedFilters={setAppliedFilters}
            />

            <Stack direction="row" spacing={1} p={2}>
              {Object.values(InitiationDayFilter).map((label) => (
                <Chip
                  key={label}
                  label={capitalize(label)}
                  variant={selectedTime === label ? "filled" : "outlined"}
                  color="default" // keep this neutral, since we're overriding styles
                  onClick={() => handleTimeFilter(label)}
                  sx={{
                    fontWeight: 500,
                    backgroundColor: selectedTime === label ? "#EEE8ED" : "transparent",
                    color: selectedTime === label ? "#541C4C" : "inherit",
                    borderColor: selectedTime === label ? "#EEE8ED" : "#ccc", // optional for outlined
                  }}
                />
              ))}
            </Stack>
            <ReactTable
              data={allInvoices}
              columns={columns}
              isLoading={isLoadingAllInvoices}
              enableRowVirtualization={true}
              enableColumnVirtualization={true}
              enableRowActions={true}
              onColumnFiltersChange={setColumnFilters}
              additionalState={{
                columnFilters,
              }}
              displayColumnDefOptions={{
                "mrt-row-actions": {
                  header: "Credit Note",
                  size: 150,
                },
              }}
              renderRowActions={({ row }) => (
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <CustomButton
                    variant="outlined"
                    size="small"
                    disabled={!row.original.isCreditNote}
                    onClick={() => {
                      handleDownloadCN(row.original.id);
                    }}
                  >
                    Download
                  </CustomButton>
                </Box>
              )}
              additionalInitialState={{
                columnPinning: { left: [], right: ["mrt-row-actions"] },
                columnVisibility: {
                  gstin: false,
                  grnAmount: false,
                  vendorCode: false,
                  invoiceAccountingDate: false,
                  dueDate: false,
                },
              }}
            />
          </Box>
        </>
      )}
      {openFilter && (
        <VendorSideFilter
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          setAppliedFilters={setAppliedFilters}
          appliedFilters={appliedFilters}
        />
      )}
    </LoggedInSkeleton>
  );
}
